(function () {
if (customElements.get('predictive-search')) return;
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.requestId = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input || !this.input.form || !this.predictiveSearchResults) return;
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input?.removeAttribute('aria-activedescendant');
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    const shouldReset = super.shouldResetForm();
    super.onFormReset(event);
    if (shouldReset) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) { this.close(true); return; }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    // Activate a selected suggestion before the browser's implicit form submit,
    // so Enter follows it once and the normal search button remains usable.
    if (event.code === 'Enter' && !event.isComposing && this.hasAttribute('open') && this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
      return;
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText && previousTerm) {
      const first = currentButtonText.indexOf(previousTerm);
      // Treat user input as literal text, including punctuation such as "[" or "(".
      if (first < 0 || currentButtonText.indexOf(previousTerm, first + previousTerm.length) !== -1) return;
      searchForTextElement.textContent = currentButtonText.slice(0, first) + newTerm + currentButtonText.slice(first + previousTerm.length);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;
    if (!activeElement.id) activeElement.id = (activeElement.parentElement.id || this.input.id || 'predictive-search') + '-option-' + activeElementIndex;
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.scrollSelectedOptionIntoView(activeElement);
  }

  scrollSelectedOptionIntoView(option) {
    const results = this.predictiveSearchResults;
    if (!results || results.clientHeight <= 0 || results.scrollHeight <= results.clientHeight) return;

    const resultsBounds = results.getBoundingClientRect();
    const optionBounds = option.getBoundingClientRect();
    if (resultsBounds.height <= 0 || optionBounds.height <= 0) return;
    const top = resultsBounds.top + results.clientTop;
    let bottom = top + results.clientHeight;
    const footer = results.querySelector('.predictive-search__search-for-button');
    if (footer && !footer.contains(option) && getComputedStyle(footer).position === 'sticky') {
      const footerBounds = footer.getBoundingClientRect();
      if (footerBounds.height > 0 && footerBounds.bottom > top && footerBounds.top < bottom) {
        bottom = Math.max(top, footerBounds.top);
      }
    }
    if (bottom <= top) return;
    let adjustment = 0;
    if (optionBounds.top < top || optionBounds.height > bottom - top) {
      adjustment = optionBounds.top - top;
    } else if (optionBounds.bottom > bottom) {
      adjustment = optionBounds.bottom - bottom;
    }
    if (!adjustment) return;

    // Scroll only the suggestions. scrollIntoView can also move the modal or
    // the page behind it while keyboard focus intentionally stays in the input.
    results.scrollTop = Math.max(0, Math.min(results.scrollTop + adjustment, results.scrollHeight - results.clientHeight));
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    if (!this.predictiveSearchResults || !searchTerm) return false;
    // Ignore superseded responses instead of aborting fetch: installed app
    // observers can otherwise leak an uncaught rejection when a request aborts.
    const requestId = ++this.requestId;
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return true;
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const route = window.routes?.predictive_search_url || root + 'search/suggest';
      const response = await fetch(`${route}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`);
      if (!response.ok) throw new Error('Search suggestions unavailable');
      const text = await response.text();
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Search suggestions unavailable');
      if (requestId !== this.requestId || this.getQuery() !== searchTerm || !this.isConnected) return false;
      const resultsMarkup = section.innerHTML;
      document.querySelectorAll('predictive-search').forEach((instance) => {
        if (instance.cachedResults) instance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
      return true;
    } catch (error) {
      if (requestId !== this.requestId || error?.name === 'AbortError' || error?.code === 20) return false;
      // The ordinary localized search form remains usable if suggestions fail.
      this.close();
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
      return false;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    clearTimeout(this.statusTimeout);
    this.statusTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    const inputBottom = this.input ? this.input.getBoundingClientRect().bottom : 0;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.resultsMaxHeight = Math.max(0, viewportHeight - inputBottom - 16);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults || !this.input) return;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    ++this.requestId;
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (!this.input || !this.predictiveSearchResults) return;
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  disconnectedCallback() {
    this.close();
    clearTimeout(this.statusTimeout);
  }
}

customElements.define('predictive-search', PredictiveSearch);
})();
